import { useEffect, useRef, useState } from 'react'

import { api } from '@/lib/api'
import type { PlanRequest, PlanResponse } from '@/lib/types'

/** Délai avant recalcul, en millisecondes. */
const DEBOUNCE_MS = 250

interface PlanState {
  plan: PlanResponse | null
  /** Erreur de la requête elle-même (réseau, 4xx). Distincte des erreurs
   *  métier du plan, qui vivent dans `plan.errors`. */
  error: string | null
  loading: boolean
}

/**
 * Calcule le plan d'ordre à chaque changement de saisie.
 *
 * Deux précautions, l'une et l'autre visibles à l'usage sur mobile :
 *
 * - **anti-rebond** : taper « 0.25 » déclencherait quatre requêtes ; on attend
 *   250 ms de calme avant d'appeler.
 * - **annulation** : la requête précédente est abandonnée (`AbortController`),
 *   sinon une réponse lente pourrait écraser un résultat plus récent et
 *   afficher un risque qui ne correspond plus à ce qui est à l'écran.
 *
 * `request` à `null` signifie « saisie incomplète » : on n'appelle pas et on
 * efface le plan précédent, plutôt que de laisser un chiffre périmé.
 */
export function usePlan(request: PlanRequest | null): PlanState {
  const [state, setState] = useState<PlanState>({ plan: null, error: null, loading: false })
  const controller = useRef<AbortController | null>(null)
  const serialised = JSON.stringify(request)

  useEffect(() => {
    controller.current?.abort()

    if (request === null) {
      setState({ plan: null, error: null, loading: false })
      return
    }

    const abort = new AbortController()
    controller.current = abort
    setState((previous) => ({ ...previous, loading: true }))

    const timer = window.setTimeout(async () => {
      try {
        const plan = await api.plan(request, abort.signal)
        if (!abort.signal.aborted) setState({ plan, error: null, loading: false })
      } catch (error) {
        if (abort.signal.aborted) return
        const message = error instanceof Error ? error.message : 'Erreur inconnue'
        setState({ plan: null, error: message, loading: false })
      }
    }, DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timer)
      abort.abort()
    }
    // `serialised` capture le contenu de la requête : deux objets différents
    // mais de même contenu ne doivent pas relancer un calcul.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialised])

  return state
}
